import { IValidationResult, IWithId, Validator } from './interfaces';
import {
  createValidateSubTypeFunction,
  validateAllOrNone,
  validateAmountTotal,
  validateAuthority,
  validateBuyerAtClosing,
  validateDateFrom,
  validateDateTo,
  validateEndorsement,
  validateLabel,
  validateName,
  validateOptionalAmountPerDay,
  validateOptionalBuyerAtClosing,
  validateOptionalBuyerBeforeClosing,
  validateOptionalDateFrom,
  validateOptionalDateTo,
  validateOptionalLabel,
  validateOptionalMonthlyPayment,
  validateOptionalMonths,
  validateOptionalPayee,
  validateOptionalSellerAtClosing,
  validateOverridden,
  validatePaidByOthers,
  validatePayee,
  validatePercentageOfInterest,
  validateSellerAtClosing,
  validateShoppedFor,
  validatorsForAmountProperties,
  validatorsForSharedProperties,
} from './properties';
import {
  IAggregateAdjustmentViewItem,
  IBuyerCreditFromSellerViewItem,
  IBuyerCreditFromThirdPartyViewItem,
  IBuyerDepositViewItem,
  IBuyerReimbursementFromSellerViewItem,
  IBuyerReimbursementOtherFromSellerViewItem,
  IBuyerReimbursementOtherToSellerViewItem,
  IBuyerReimbursementToSellerViewItem,
  IBuyerReimbursementToThirdPartyViewItem,
  ICDV3ViewItem,
  IEndorsementOtherViewItem,
  IEndorsementViewItem,
  IEscrowAtClosingOtherViewItem,
  IEscrowAtClosingViewItem,
  IExistingLoanAssumedViewItem,
  IHOAFeeViewItem,
  IInterestPrepaymentViewItem,
  ILenderCreditViewItem,
  INonInterestPrepaymentViewItem,
  IOriginationFeeOtherViewItem,
  IOtherFeeViewItem,
  IOtherLoanCostViewItem,
  IPayoffViewItem,
  IPercentageOfInterestViewItem,
  IPersonalPropertySaleViewItem,
  IPrepaymentOtherViewItem,
  IPropertyTaxesViewItem,
  IRecordingFeeOtherViewItem,
  IRecordingFeeViewItem,
  ISalesTaxViewItem,
  ISellerCreditViewItem,
  ISellerExcessDepositViewItem,
  ITaxOtherViewItem,
  ITaxViewItem,
  ITitleFeeOtherViewItem,
  ITitleFeeViewItem,
} from '../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { eTitleFees } from '../../ui-toolkit/enums/eTitleFees';
import { eTaxType } from '../../ui-toolkit/enums/eTaxType';
import { eReimbursements } from '../../ui-toolkit/enums/eReimbursments';
import { eRecordingFees } from '../../ui-toolkit/enums/eRecordingFees';
import { eNonInterestPrepayments } from '../../ui-toolkit/enums/eNonInterestPrepayments';
import { eCDV3ItemTypes } from '../../ui-toolkit/enums/eCDV3ItemTypes';
import { eEscrowPrepaymentsAtClosing } from '../../ui-toolkit/enums/eEscrowPrepaymentsAtClosing';
import { eHOAFees } from '../../ui-toolkit/enums/eHOAFees';
import { Logger } from '../../ui-toolkit/logging/LoggerService';
import { IOrderDetailsData } from '../../ui-toolkit/orders/interfaces/IOrderDetailsData';

const createItemValidator =
  <T extends IWithId, U>(validators: Validator<T, U>[]) =>
  (item: T, otherInfo?: U) =>
    validators.map((validator) => validator(item, otherInfo));

export const validatePercentageOfInterestProperties = createItemValidator<
  IPercentageOfInterestViewItem,
  IOrderDetailsData
>([
  validatePercentageOfInterest,
  validateOverridden,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateOriginationFeeOtherProperties = createItemValidator<
  IOriginationFeeOtherViewItem,
  IOrderDetailsData
>([validateLabel, ...validatorsForAmountProperties, ...validatorsForSharedProperties]);

export const validateEndorsementProperties = createItemValidator<IEndorsementViewItem, IOrderDetailsData>([
  validateEndorsement,
  validateOverridden,
  validateShoppedFor,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
  validateAmountTotal,
]);

export const validateEndorsementOtherProperties = createItemValidator<IEndorsementOtherViewItem, IOrderDetailsData>([
  validateLabel,
  validateShoppedFor,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateTitleFeeProperties = createItemValidator<ITitleFeeViewItem, IOrderDetailsData>([
  createValidateSubTypeFunction(eTitleFees),
  validateShoppedFor,
  validateOverridden,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
  validateAmountTotal,
]);

export const validateTitleFeeOtherProperties = createItemValidator<ITitleFeeOtherViewItem, IOrderDetailsData>([
  validateLabel,
  validateShoppedFor,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateOtherLoanCostProperties = createItemValidator<IOtherLoanCostViewItem, IOrderDetailsData>([
  validateOptionalLabel,
  validateShoppedFor,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateRecordingFeeProperties = createItemValidator<IRecordingFeeViewItem, IOrderDetailsData>([
  createValidateSubTypeFunction(eRecordingFees),
  validateOverridden,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateRecordingFeeOtherProperties = createItemValidator<IRecordingFeeOtherViewItem, IOrderDetailsData>([
  validateOptionalLabel,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateTaxProperties = createItemValidator<ITaxViewItem, IOrderDetailsData>([
  validateName,
  validateAuthority,
  createValidateSubTypeFunction(eTaxType),
  validateOverridden,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateTaxOtherProperties = createItemValidator<ITaxOtherViewItem, IOrderDetailsData>([
  validateOptionalLabel,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateNonInterestPrepaymentProperties = createItemValidator<
  INonInterestPrepaymentViewItem,
  IOrderDetailsData
>([
  validateOptionalMonths,
  createValidateSubTypeFunction(eNonInterestPrepayments),
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateInterestPrepaymentProperties = createItemValidator<IInterestPrepaymentViewItem, IOrderDetailsData>(
  [
    validateOptionalAmountPerDay,
    validateOptionalDateFrom,
    validateOptionalDateTo,
    validateOptionalPayee,
    ...validatorsForAmountProperties,
    ...validatorsForSharedProperties,
  ]
);

export const validatePrepaymentOtherProperties = createItemValidator<IPrepaymentOtherViewItem, IOrderDetailsData>([
  validateOptionalLabel,
  validateOptionalMonths,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateEscrowAtClosingProperties = createItemValidator<IEscrowAtClosingViewItem, IOrderDetailsData>([
  validateOptionalMonthlyPayment,
  validateOptionalMonths,
  createValidateSubTypeFunction(eEscrowPrepaymentsAtClosing),
  //   validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateEscrowAtClosingOtherProperties = createItemValidator<
  IEscrowAtClosingOtherViewItem,
  IOrderDetailsData
>([
  validateOptionalLabel,
  validateOptionalMonthlyPayment,
  validateOptionalMonths,
  //   validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateAggregateAdjustmentProperties = createItemValidator<
  IAggregateAdjustmentViewItem,
  IOrderDetailsData
>([...validatorsForAmountProperties, ...validatorsForSharedProperties]);

export const validateOtherFeeProperties = createItemValidator<IOtherFeeViewItem, IOrderDetailsData>([
  validateOptionalLabel,
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateLenderCreditProperties = createItemValidator<ILenderCreditViewItem, IOrderDetailsData>([
  validateOptionalBuyerAtClosing, // FYI: this does not match API because in UI empty amount translates to DELETE
  ...validatorsForSharedProperties,
]);

export const validatePayoffProperties = createItemValidator<IPayoffViewItem, IOrderDetailsData>([
  validateOptionalLabel,
  validatePayee,
  validateOptionalBuyerAtClosing,
  validateOptionalSellerAtClosing,
  ...validatorsForSharedProperties,
]);

export const validatePersonalPropertySaleProperties = createItemValidator<
  IPersonalPropertySaleViewItem,
  IOrderDetailsData
>([validateOptionalBuyerAtClosing, ...validatorsForSharedProperties]);

export const validateBuyerReimbursementToThirdPartyProperties = createItemValidator<
  IBuyerReimbursementToThirdPartyViewItem,
  IOrderDetailsData
>([validateLabel, validateOptionalPayee, validateBuyerAtClosing, ...validatorsForSharedProperties]);

export const validateBuyerReimbursementToSellerProperties = createItemValidator<
  IBuyerReimbursementToSellerViewItem,
  IOrderDetailsData
>([
  createValidateSubTypeFunction(eReimbursements),
  validateAllOrNone(validateDateFrom, validateDateTo, validateBuyerAtClosing),
  ...validatorsForSharedProperties,
]);

export const validateBuyerReimbursementOtherToSellerProperties = createItemValidator<
  IBuyerReimbursementOtherToSellerViewItem,
  IOrderDetailsData
>([
  validateLabel,
  validateOptionalDateFrom,
  validateOptionalDateTo,
  validateBuyerAtClosing,
  ...validatorsForSharedProperties,
]);

export const validateBuyerDepositProperties = createItemValidator<IBuyerDepositViewItem, IOrderDetailsData>([
  validateOptionalBuyerBeforeClosing,
  ...validatorsForSharedProperties,
]);

export const validateExistingLoanAssumedProperties = createItemValidator<
  IExistingLoanAssumedViewItem,
  IOrderDetailsData
>([validateOptionalSellerAtClosing, ...validatorsForSharedProperties]);

export const validateSellerCreditProperties = createItemValidator<ISellerCreditViewItem, IOrderDetailsData>([
  validateOptionalSellerAtClosing,
  ...validatorsForSharedProperties,
]);

export const validateBuyerCreditFromSellerProperties = createItemValidator<
  IBuyerCreditFromSellerViewItem,
  IOrderDetailsData
>([validateLabel, validateOptionalPayee, validateSellerAtClosing, ...validatorsForSharedProperties]);

export const validateBuyerCreditFromThirdPartyProperties = createItemValidator<
  IBuyerCreditFromThirdPartyViewItem,
  IOrderDetailsData
>([validateLabel, validateOptionalPayee, validatePaidByOthers, ...validatorsForSharedProperties]);

export const validateBuyerReimbursementFromSellerProperties = createItemValidator<
  IBuyerReimbursementFromSellerViewItem,
  IOrderDetailsData
>([
  createValidateSubTypeFunction(eReimbursements),
  validateAllOrNone(validateDateFrom, validateDateTo, validateSellerAtClosing),
  ...validatorsForSharedProperties,
]);

export const validateBuyerReimbursementOtherFromSellerProperties = createItemValidator<
  IBuyerReimbursementOtherFromSellerViewItem,
  IOrderDetailsData
>([validateLabel, validateDateFrom, validateDateTo, validateSellerAtClosing, ...validatorsForSharedProperties]);

export const validateSellerExcessDepositProperties = createItemValidator<
  ISellerExcessDepositViewItem,
  IOrderDetailsData
>([validateOptionalSellerAtClosing, ...validatorsForSharedProperties]);

export const validateHOAFeeProperties = createItemValidator<IHOAFeeViewItem, IOrderDetailsData>([
  //    [Type] [Sub Type] to [Payee] Pd Through [Date]
  validateOptionalPayee,
  createValidateSubTypeFunction(eHOAFees),
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validatePropertyTaxesProperties = createItemValidator<IPropertyTaxesViewItem, IOrderDetailsData>([
  // [Period] [Type] to [Payee] ([Parcel No.]) Pd Through [Date]
  validateOptionalPayee,
  ...validatorsForAmountProperties,
  ...validatorsForSharedProperties,
]);

export const validateSalesTax = createItemValidator<ISalesTaxViewItem, IOrderDetailsData>([
  ...validatorsForAmountProperties,
]);

const typeToValidatorMap = {
  [eCDV3ItemTypes.percentage_of_loan_amount]: validatePercentageOfInterestProperties,
  [eCDV3ItemTypes.origination_fee_other]: validateOriginationFeeOtherProperties,
  [eCDV3ItemTypes.endorsement]: validateEndorsementProperties,
  [eCDV3ItemTypes.endorsement_other]: validateEndorsementOtherProperties,
  [eCDV3ItemTypes.title_fee]: validateTitleFeeProperties,
  [eCDV3ItemTypes.title_fee_other]: validateTitleFeeOtherProperties,
  [eCDV3ItemTypes.other_loan_cost]: validateOtherLoanCostProperties,
  [eCDV3ItemTypes.recording_fee]: validateRecordingFeeProperties,
  [eCDV3ItemTypes.recording_fee_other]: validateRecordingFeeOtherProperties,
  [eCDV3ItemTypes.tax]: validateTaxProperties,
  [eCDV3ItemTypes.tax_other]: validateTaxOtherProperties,
  [eCDV3ItemTypes.non_interest_prepayment]: validateNonInterestPrepaymentProperties,
  [eCDV3ItemTypes.interest_prepayment]: validateInterestPrepaymentProperties,
  [eCDV3ItemTypes.prepayment_other]: validatePrepaymentOtherProperties,
  [eCDV3ItemTypes.escrow_at_closing]: validateEscrowAtClosingProperties,
  [eCDV3ItemTypes.escrow_at_closing_other]: validateEscrowAtClosingOtherProperties,
  [eCDV3ItemTypes.aggregate_adjustment]: validateAggregateAdjustmentProperties,
  [eCDV3ItemTypes.other_fee]: validateOtherFeeProperties,
  [eCDV3ItemTypes.lender_credit]: validateLenderCreditProperties,
  [eCDV3ItemTypes.payoff]: validatePayoffProperties,
  [eCDV3ItemTypes.personal_property_sale]: validatePersonalPropertySaleProperties,
  [eCDV3ItemTypes.buyer_reimbursement_to_third_party]: validateBuyerReimbursementToThirdPartyProperties,
  [eCDV3ItemTypes.buyer_reimbursement_to_seller]: validateBuyerReimbursementToSellerProperties,
  [eCDV3ItemTypes.buyer_reimbursement_other_to_seller]: validateBuyerReimbursementOtherToSellerProperties,
  [eCDV3ItemTypes.buyer_deposit]: validateBuyerDepositProperties,
  [eCDV3ItemTypes.existing_loans_assumed]: validateExistingLoanAssumedProperties,
  [eCDV3ItemTypes.seller_credit]: validateSellerCreditProperties,
  [eCDV3ItemTypes.buyer_credit_from_seller]: validateBuyerCreditFromSellerProperties,
  [eCDV3ItemTypes.buyer_credit_from_third_party]: validateBuyerCreditFromThirdPartyProperties,
  [eCDV3ItemTypes.buyer_reimbursement_from_seller]: validateBuyerReimbursementFromSellerProperties,
  [eCDV3ItemTypes.buyer_reimbursement_other_from_seller]: validateBuyerReimbursementOtherFromSellerProperties,
  [eCDV3ItemTypes.seller_excess_deposit]: validateSellerExcessDepositProperties,
  [eCDV3ItemTypes.hoa_fee]: validateHOAFeeProperties,
  [eCDV3ItemTypes.property_taxes]: validatePropertyTaxesProperties,
  [eCDV3ItemTypes.sales_tax]: validateSalesTax,
};

export const validateItem =
  (orderDetails: IOrderDetailsData) =>
  (item: ICDV3ViewItem): IValidationResult => {
    let propertyResults: IValidationResult[] = [];
    const validator = typeToValidatorMap[item.type];
    if (validator) {
      propertyResults = validator(item, orderDetails);
    } else {
      Logger.warn('ValidationService does not implement a validator', item);
    }

    const isValid = propertyResults.reduce((validity, result) => validity && result.valid, true);

    return {
      id: item.id,
      items: propertyResults,
      message: isValid ? 'OK' : `${item.type} is invalid`,
      valid: isValid,
    };
  };
